// ./src/utils/api/EventPolicyService.ts
//
// EventPolicy — attaches an account-level Policy to an event (a REFERENCE, not a
// copy: the policy detail is resolved live from the account policy). Manages the
// attach/detach + is-required + order, and surfaces guest acceptance records.
// OpResult-native. A multi-id update/remove is a bulk op (server restricts a
// bulk update to isRequired / status).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

// The referenced policy's detail. policyText is included so the list can tell a
// document policy from a simple checkbox (and gate the print action); the list is
// small (a handful per event), so the extra weight is negligible.
const POLICY_SUMMARY_FIELDS = `
  id
  name
  label
  policyText
  contentFormat
  minAge
  maxAge
  requireInitials
  requireSignature
  acceptanceMethod
  status
`;

const EVENT_POLICY_FIELDS = `
  id
  accountId
  eventId
  policyId
  isRequired
  orderNo
  status
  rights
  createdAt
  updatedAt
  policy { ${POLICY_SUMMARY_FIELDS} }
  acceptances { id acceptanceStatus }
`;

const EVENT_POLICY_LIST_QUERY = gql`
  query EventPolicyList($filter: EventPolicyFilter, $params: PaginationAndSorting) {
    eventPolicyList(filter: $filter, params: $params) {
      code
      data { ${EVENT_POLICY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_POLICY_GET_QUERY = gql`
  query EventPolicyGet($id: ID) {
    eventPolicyGet(id: $id) {
      code
      data { ${EVENT_POLICY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_POLICY_CREATE_MUTATION = gql`
  mutation EventPolicyCreate($params: EventPolicyInput) {
    eventPolicyCreate(params: $params) {
      code
      data { ${EVENT_POLICY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_POLICY_UPDATE_MUTATION = gql`
  mutation EventPolicyUpdate($where: EventPolicyWhereInput, $params: EventPolicyInput) {
    eventPolicyUpdate(where: $where, params: $params) {
      code
      data { ${EVENT_POLICY_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const EVENT_POLICY_REMOVE_MUTATION = gql`
  mutation EventPolicyRemove($where: EventPolicyWhereInput) {
    eventPolicyRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface EventPolicySummaryPolicy {
  id?: string;
  name?: string | null;
  label?: string | null;
  policyText?: string | null;
  contentFormat?: string | null;
  minAge?: number | null;
  maxAge?: number | null;
  requireInitials?: boolean | null;
  requireSignature?: boolean | null;
  /** 'sign' | 'checkbox' (#164). */
  acceptanceMethod?: string | null;
  status?: number;
}

export interface PolicyAcceptanceLite {
  id?: string;
  acceptanceStatus?: string | null;
}

export interface EventPolicy {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  policyId?: string | null;
  /** Must be accepted to proceed (blocking). */
  isRequired?: boolean | null;
  orderNo?: number | null;
  status?: number;
  rights?: string[] | null;
  /** The referenced account policy's detail (resolved live). */
  policy?: EventPolicySummaryPolicy | null;
  /** Guest consent records for this attached policy. */
  acceptances?: PolicyAcceptanceLite[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface EventPolicyInput {
  eventId?: string | null;
  policyId?: string | null;
  isRequired?: boolean | null;
  orderNo?: number | null;
  status?: number | null;
}

export interface EventPolicyFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  policyId?: string[] | null;
  status?: number[] | null;
}

export interface EventPolicyWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

export class EventPolicyService {
  static async list(
    eventId: string,
    statuses?: number[],
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const filter: EventPolicyFilter = { eventId: [eventId] };

    if (statuses && statuses.length > 0) {
      filter.status = statuses;
    }

    return ApiService.executeQuery(EVENT_POLICY_LIST_QUERY, {
      filter,
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_POLICY_GET_QUERY, { id });
  }

  /** Attach an account policy to an event. */
  static async attach(params: EventPolicyInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_POLICY_CREATE_MUTATION, { params });
  }

  /** Update one (id) or many (ids) attachments. A multi-id update may only change isRequired / status. */
  static async update(idOrIds: string | string[], params: EventPolicyInput): Promise<OpResult> {
    return ApiService.executeMutation(EVENT_POLICY_UPDATE_MUTATION, { where: { id: idOrIds }, params });
  }

  /** Detach one (id) or many (ids) attachments. */
  static async remove(idOrIds: string | string[]): Promise<OpResult> {
    if (Array.isArray(idOrIds) && idOrIds.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(EVENT_POLICY_REMOVE_MUTATION, { where: { id: idOrIds } });
  }
}
